// src/neetjee_guru/components/academicPlanner/ConfigurationPanel.js
import React, { Component } from "react";
import PlannerCreationWizard from "./PlannerCreationWizard";
import BulkUploadModal from "./BulkUploadModal";
import EditPlannerModal from "./EditPlannerModal";
import EditScopeMappingModal from "./EditScopeMappingModal";

/**
 * ConfigurationPanel — Clean Admin-only configuration section for Academic Planner.
 *
 * Streamlined layout focusing directly on Master Planner Registry & Actions.
 */
class ConfigurationPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showCreateWizard: false,
      showBulkUpload: false,
      bulkUploadPlannerId: null,
      editingPlanner: null, // Planner object currently being edited
      editingMapping: null  // Planner object whose map is currently being edited
    };
  }

  handleEditPlanner = (planner) => {
    this.setState({ editingPlanner: planner });
  };

  handleEditMapping = (planner) => {
    this.setState({ editingMapping: planner });
  };

  handleSavePlannerSuccess = (updatedData) => {
    this.setState({ editingPlanner: null });
    if (this.props.onCreatePlanner) {
      this.props.onCreatePlanner(); // Trigger refresh in parent
    }
  };

  handleSaveMappingSuccess = (updatedMapData) => {
    this.setState({ editingMapping: null });
    if (this.props.onCreatePlanner) {
      this.props.onCreatePlanner(); // Trigger refresh in parent
    }
  };

  render() {
    const {
      showCreateWizard, showBulkUpload, bulkUploadPlannerId,
      editingPlanner, editingMapping
    } = this.state;

    const {
      plannersList, branchesList, sectionsList, academicYearsList, institutionId,
      apolloClient, onCreatePlanner, selectedPlannerId, onPlannerSelect, onBulkUpload
    } = this.props;

    return (
      <div className="configuration-panel">
        {/* ── Master Planner Registry Card (Primary Clean UI) ───────────────────── */}
        <div className="card border-0 mb-4" style={{ borderRadius: 16, boxShadow: "0 6px 24px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0" }}>
          <div className="card-header bg-white py-4 px-4 d-flex align-items-center justify-content-between flex-wrap gap-3 border-bottom">
            <div>
              <h5 className="font-weight-bold text-dark m-0 d-flex align-items-center gap-2">
                <i className="fal fa-sitemap text-primary mr-2" style={{ fontSize: 22 }} />
                Master Planner Registry
              </h5>
              <span className="small text-muted mt-1 d-block font-weight-semibold">
                Manage {plannersList ? plannersList.length : 0} configured academic master planners and their campus scope assignments.
              </span>
            </div>

            <div className="d-flex align-items-center gap-3">
              <span className="badge badge-warning text-dark px-3 py-2" style={{ borderRadius: 20, fontSize: "12px", fontWeight: 700 }}>
                <i className="fal fa-shield-alt mr-1" /> Admin Only
              </span>
              <button
                className="btn btn-primary font-weight-bold shadow-sm d-flex align-items-center gap-2"
                style={{ borderRadius: 24, padding: "10px 22px", fontSize: "14px" }}
                onClick={() => this.setState({ showCreateWizard: true })}
              >
                <i className="fal fa-plus-circle" style={{ fontSize: 18 }} />
                Create New Planner
              </button>
            </div>
          </div>

          <div className="card-body p-0">
            {(!plannersList || plannersList.length === 0) ? (
              <div className="text-center py-5 text-muted">
                <i className="fal fa-calendar-plus mb-3 text-primary d-block" style={{ fontSize: 52, opacity: 0.8 }} />
                <h6 className="font-weight-bold text-dark mb-2">No Academic Planners Configured</h6>
                <p className="small text-muted mx-auto mb-4" style={{ maxWidth: 420 }}>
                  Click below to launch the wizard and create your first Master Planner for the academic year.
                </p>
                <button
                  className="btn btn-primary font-weight-bold px-4 py-2 shadow-sm"
                  style={{ borderRadius: 20 }}
                  onClick={() => this.setState({ showCreateWizard: true })}
                >
                  <i className="fal fa-magic mr-2" /> Launch Creation Wizard
                </button>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-hover align-middle mb-0" style={{ fontSize: "13.5px" }}>
                  <thead style={{ background: "#f8fafc", color: "#475569", fontWeight: 700, textTransform: "uppercase", fontSize: "11.5px", letterSpacing: "0.6px" }}>
                    <tr>
                      <th className="py-3.5 px-4 border-0" style={{ width: 70 }}>ID</th>
                      <th className="py-3.5 border-0">Planner Details</th>
                      <th className="py-3.5 border-0">Scope Mapping</th>
                      <th className="py-3.5 border-0">Status</th>
                      <th className="py-3.5 text-right px-4 border-0">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plannersList.map(planner => {
                      const isActiveView = String(planner.id) === String(selectedPlannerId);
                      const isAllBranches = planner.map_details && planner.map_details.branch_ids === "ALL";
                      
                      return (
                        <tr key={planner.id} style={{ background: isActiveView ? "#faf5ff" : "#fff", borderBottom: "1px solid #f1f5f9" }}>
                          <td className="px-4 py-3.5 font-weight-bold text-muted" style={{ fontSize: "14px" }}>
                            #{planner.id}
                          </td>
                          
                          <td className="py-3.5">
                            <div className="font-weight-bold text-dark" style={{ fontSize: "15px" }}>
                              {planner.academic_planner}
                              {isActiveView && (
                                <span className="badge badge-primary ml-2" style={{ fontSize: "10.5px", padding: "4px 10px", borderRadius: 12 }}>
                                  Active View
                                </span>
                              )}
                            </div>
                            <div className="text-muted small mt-1 font-weight-semibold" style={{ fontSize: "12.5px" }}>
                              <i className="fal fa-tag mr-1 text-primary" /> {planner.stream_name || "Standard"}
                              <span className="mx-2 text-black-50">|</span>
                              <i className="fal fa-calendar-alt mr-1 text-muted" /> Academic Year #{planner.academic_year_id}
                            </div>
                          </td>
                          
                          <td className="py-3.5">
                            <div className="d-flex flex-column gap-1 align-items-start">
                              <span className={`badge ${isAllBranches ? "badge-success" : "badge-info"}`}
                                style={{ fontSize: "11.5px", padding: "5px 12px", borderRadius: 12 }}>
                                <i className={`fal ${isAllBranches ? "fa-globe" : "fa-building"} mr-1`} />
                                {isAllBranches ? "Institute Master (All)" : `Branches: ${planner.map_details ? planner.map_details.branch_ids : "Unmapped"}`}
                              </span>
                              <span className="badge badge-light border text-muted mt-1" style={{ fontSize: "11px", borderRadius: 10, padding: "3px 8px" }}>
                                <i className="fal fa-users-class mr-1 text-secondary" />
                                Sections: {planner.map_details ? (planner.map_details.section_ids || "ALL") : "ALL"}
                              </span>
                            </div>
                          </td>
                          
                          <td className="py-3.5">
                            <span className={`badge ${planner.estatus === 1 || planner.estatus === undefined ? "badge-light text-success border-success" : "badge-light text-secondary border"}`}
                              style={{ fontSize: "11.5px", padding: "5px 12px", borderRadius: 12, borderWidth: "1px", borderStyle: "solid", fontWeight: 700 }}>
                              <i className={`fal ${planner.estatus === 1 || planner.estatus === undefined ? "fa-check-circle" : "fa-times-circle"} mr-1`} />
                              {planner.estatus === 1 || planner.estatus === undefined ? "Active" : "Inactive"}
                            </span>
                          </td>
                          
                          {/* ── Large Prominent Action Buttons ── */}
                          <td className="py-3.5 text-right px-4">
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              
                              {/* Bulk Upload CSV Data — Large Action Icon */}
                              <button
                                type="button"
                                className="btn shadow-sm d-inline-flex align-items-center justify-content-center"
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 12,
                                  background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                                  border: "1.5px solid #fde68a",
                                  color: "#d97706",
                                  transition: "transform 0.15s, box-shadow 0.15s"
                                }}
                                onClick={() => this.setState({ showBulkUpload: true, bulkUploadPlannerId: planner.id })}
                                title="Bulk Upload Topics via CSV"
                              >
                                <i className="fal fa-file-csv" style={{ fontSize: 19 }} />
                              </button>

                              {/* Edit Planner Config — Large Action Icon */}
                              <button
                                type="button"
                                className="btn shadow-sm d-inline-flex align-items-center justify-content-center"
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 12,
                                  background: "linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)",
                                  border: "1.5px solid #ddd6fe",
                                  color: "#7c3aed",
                                  transition: "transform 0.15s, box-shadow 0.15s"
                                }}
                                onClick={() => this.handleEditPlanner(planner)}
                                title="Edit Planner Configuration (Title, Stream, Academic Year)"
                              >
                                <i className="fal fa-edit" style={{ fontSize: 19 }} />
                              </button>

                              {/* Edit Scope Mapping — Large Action Icon */}
                              <button
                                type="button"
                                className="btn shadow-sm d-inline-flex align-items-center justify-content-center"
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 12,
                                  background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                  border: "1.5px solid #bbf7d0",
                                  color: "#059669",
                                  transition: "transform 0.15s, box-shadow 0.15s"
                                }}
                                onClick={() => this.handleEditMapping(planner)}
                                title="Edit Scope & Branch Mappings"
                              >
                                <i className="fal fa-network-wired" style={{ fontSize: 19 }} />
                              </button>

                              {/* View Schedule — Prominent View Button */}
                              <button
                                type="button"
                                className={`btn font-weight-bold shadow-sm d-inline-flex align-items-center gap-1.5 ${isActiveView ? "btn-primary" : "btn-outline-primary"}`}
                                style={{
                                  height: 40,
                                  padding: "0 16px",
                                  borderRadius: 12,
                                  fontSize: "13px"
                                }}
                                onClick={() => {
                                  if (onPlannerSelect) onPlannerSelect(planner.id);
                                }}
                                title="Switch active schedule view to this planner"
                              >
                                <i className="fal fa-eye" style={{ fontSize: 16 }} />
                                View
                              </button>

                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── Modals ────────────────────────────────────────────────────────────── */}
        <PlannerCreationWizard
          show={showCreateWizard}
          onHide={() => this.setState({ showCreateWizard: false })}
          onCreatePlanner={onCreatePlanner}
          branchesList={branchesList}
          sectionsList={sectionsList}
          academicYearsList={academicYearsList}
          institutionId={institutionId}
          apolloClient={apolloClient}
        />

        <BulkUploadModal
          show={showBulkUpload}
          onHide={() => this.setState({ showBulkUpload: false, bulkUploadPlannerId: null })}
          plannerId={bulkUploadPlannerId || selectedPlannerId}
          onBulkUpload={onBulkUpload}
        />

        <EditPlannerModal
          show={!!editingPlanner}
          onHide={() => this.setState({ editingPlanner: null })}
          planner={editingPlanner}
          academicYearsList={academicYearsList}
          apolloClient={apolloClient}
          onSavePlanner={this.handleSavePlannerSuccess}
        />

        <EditScopeMappingModal
          show={!!editingMapping}
          onHide={() => this.setState({ editingMapping: null })}
          planner={editingMapping}
          branchesList={branchesList}
          sectionsList={sectionsList}
          apolloClient={apolloClient}
          onSaveMapping={this.handleSaveMappingSuccess}
        />

      </div>
    );
  }
}

export default ConfigurationPanel;
